// SolveScaledShiftedAbsoluteLinearTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

/* ===== Utils ===== */
function gcd(a, b) { while (b !== 0) [a, b] = [b, a % b]; return Math.abs(a); }
function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;
  if (frac === 0) return `${sign}${intPart}`;
  for (let d = 1; d <= 10; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g, den = d / g;
      return intPart === 0 ? `${sign}\\dfrac{${num}}{${den}}` : `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}
const coeffX  = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const linExpr = (a, b) => `${coeffX(a)} ${b >= 0 ? "+ " + b : "- " + Math.abs(b)}`;

/* ===== Generate ===== */
export function generateTask() {
  // a ≠ 0
  let a;
  do { a = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 4) + 1); } while (a === 0);

  // b dowolne, d ∈ {2,3,4,5}, e dowolne
  const b = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 6) + 1);
  const allowedD = [2, 3, 4, 5];
  const d = allowedD[Math.floor(Math.random() * allowedD.length)];
  const e = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 5) + 1);

  // Wybieramy c tak, aby (c - e)/d ≥ 0
  const baseAbs = Math.abs(2 * a + b) || 1;
  const c = d * baseAbs + e;

  // Podstawa po przeniesieniu i podzieleniu
  const base = (c - e) / d; // ≥ 0

  // Rozwiązania ax + b = base oraz ax + b = -base
  const x1 = (base - b) / a;
  const x2 = (-base - b) / a;

  // LaTeX treści
  const fullExpr = `${d}\\left|${linExpr(a, b)}\\right| ${e >= 0 ? "+ " + e : "- " + Math.abs(e)} = ${c}`;

  // Do wyjaśnień
  const sum1 = base - b;   // ax = base - b
  const sum2 = -base - b;  // ax = -base - b

  return {
    type: "SolveScaledShiftedAbsoluteLinearTask",
    data: { a, b, d, e, c, base, x1, x2, latex: fullExpr, sum1, sum2 }
  };
}

/* ===== Task ===== */
export function renderTask({ data }) {
  return <p>Rozwiąż równanie: <InlineMath math={data.latex} /></p>;
}

export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math="x_1 =" /></span>
        <FractionInput
          value={userInput?.x1 || ""}
          onChange={v => onChange({ ...userInput, x1: v })}
          disabled={disabled}
          label="x_1"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math="x_2 =" /></span>
        <FractionInput
          value={userInput?.x2 || ""}
          onChange={v => onChange({ ...userInput, x2: v })}
          disabled={disabled}
          label="x_2"
        />
      </div>
    </div>
  );
}

/* ===== Check ===== */
export function checkAnswer({ data }, userInput) {
  const { x1, x2 } = data;
  const u1 = userInput?.x1?.parsed, u2 = userInput?.x2?.parsed;
  const ok =
    (Math.abs(u1 - x1) < 0.01 && Math.abs(u2 - x2) < 0.01) ||
    (Math.abs(u1 - x2) < 0.01 && Math.abs(u2 - x1) < 0.01);
  return { correct: ok, expected: [x1, x2] };
}

// Krótka odpowiedź: większy odstęp między x1 i x2
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div style={{ marginBottom: "0.6rem" }}>
        <InlineMath math={`x_1 = ${formatFractionLatex(data.x1)}`} />
      </div>
      <div>
        <InlineMath math={`x_2 = ${formatFractionLatex(data.x2)}`} />
      </div>
    </div>
  );
}

// Podgląd odpowiedzi użytkownika: taki sam odstęp
export function renderUserAnswer(userInput) {
  const x1 = formatLatexPreview(userInput?.x1?.raw || "", "x_1");
  const x2 = formatLatexPreview(userInput?.x2?.raw || "", "x_2");
  if (!x1 && !x2) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      {x1 && (
        <div style={{ marginBottom: "0.6rem" }}>
          <InlineMath math={x1} />
        </div>
      )}
      {x2 && (
        <div>
          <InlineMath math={x2} />
        </div>
      )}
    </div>
  );
}


/* ===== Explanation ===== */
export function renderExplanation({ data }) {
  const { a, b, d, e, c, base, x1, x2, sum1, sum2 } = data;

  const L         = linExpr(a, b);
  const baseFrac  = `\\dfrac{${c - e}}{${d}}`;
  const baseLatex = formatFractionLatex(base);
  const aX        = coeffX(a);

  // Krok 3 – rozwiązujemy obie gałęzie: najpierw dodaj/odejmij, potem od razu wynik
  const first1 = `${L} = ${baseLatex}`;
  const first2 = `${aX} = ${baseLatex} ${b >= 0 ? "- " + b : "+ " + Math.abs(b)}`;
  const first3 = `${aX} = ${formatFractionLatex(sum1)}`;
  const first4 = `x_1 = ${formatFractionLatex(x1)}`;

  const second1 = `${L} = -${baseLatex}`;
  const second2 = `${aX} = -${baseLatex} ${b >= 0 ? "- " + b : "+ " + Math.abs(b)}`;
  const second3 = `${aX} = ${formatFractionLatex(sum2)}`;
  const second4 = `x_2 = ${formatFractionLatex(x2)}`;

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Przenosimy wyraz wolny i dzielimy przez współczynnik przy wartości bezwzględnej.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${d}\\left|${L}\\right| ${e >= 0 ? "+ " + e : "- " + Math.abs(e)} = ${c}`} />
        <BlockMath math={`${d}\\left|${L}\\right| = ${c - e}`} />
        <BlockMath math={`\\left|${L}\\right| = ${baseFrac}`} />
        <BlockMath math={`\\left|${L}\\right| = ${baseLatex}`} />
      </div>

      <p><strong>Krok 2:</strong> Rozpisujemy wartość bezwzględną na dwa równania.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${L} = ${baseLatex}\\;\\;\\text{lub}\\;\\; ${L} = -${baseLatex}`} />
      </div>

      <p><strong>Krok 3:</strong> Rozwiązujemy oba równania.</p>

      <p><em>Pierwsze równanie:</em></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={first1} />
        <BlockMath math={first2} />
        <BlockMath math={first3} />
        <BlockMath math={first4} />
      </div>

      <p><em>Drugie równanie:</em></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={second1} />
        <BlockMath math={second2} />
        <BlockMath math={second3} />
        <BlockMath math={second4} />
      </div>

      <p><strong>Odpowiedź końcowa:</strong></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x_1 = ${formatFractionLatex(x1)},\\quad x_2 = ${formatFractionLatex(x2)}`} />
      </div>
    </div>
  );
}
